import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import {
  ArrowLeft,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { SearchInput } from '@/shared/components/ui/search-input'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import api from '@/admin/lib/api'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { formatPhoneDisplay } from '@/shared/lib/forms'
import { useFormatters } from '@/shared/lib/formatting'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'
import type { PaginatedResponse } from '@/shared/types/api'
interface Consumer {
  uuid: string
  email: string
  first_name: string
  last_name: string
  phone_number: string
  created_at: string | null
  user_uuid: string | null
  registration_count: number
}

type ConsumerListResponse = PaginatedResponse<Consumer>

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatJoinedDate(iso: string | null, formatDate: (iso: string) => string): string {
  if (!iso) return '-'
  return formatDate(iso)
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export function StaffConsumersPage() {
  const navigate = useNavigate()
  const { formatDate } = useFormatters()
  const [searchParams, setSearchParams] = useSearchParams()

  const [query, setQuery] = useState(searchParams.get('q') ?? '')
  const [debouncedQuery, setDebouncedQuery] = useState(query)
  const [page, setPage] = useState(Number(searchParams.get('page') ?? 1))
  const [pageSize, setPageSize] = useState(Number(searchParams.get('page_size') ?? 20))
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  // Fetch consumers via React Query
  const { data, isLoading: loading } = useQuery<ConsumerListResponse>({
    queryKey: ['staff-consumers', page, pageSize, debouncedQuery],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        page_size: String(pageSize),
      }
      if (debouncedQuery.length >= 2) params.search = debouncedQuery
      const res = await api.get<ConsumerListResponse>('/staff/consumers/', { params })
      return res.data
    },
    staleTime: 10_000,
  })

  // Sync URL params
  useEffect(() => {
    const p: Record<string, string> = {}
    if (debouncedQuery) p.q = debouncedQuery
    if (page > 1) p.page = String(page)
    if (pageSize !== 20) p.page_size = String(pageSize)
    setSearchParams(p, { replace: true })
  }, [debouncedQuery, page, pageSize, setSearchParams])

  async function handleImpersonateConsumer(consumerUuid: string) {
    try {
      const res = await api.post(`/staff/impersonate-consumer/${consumerUuid}/`)
      const token = res.data.token
      toast.success(`Opening consumer app as ${res.data.consumer.email}`)
      // Open consumer app in new tab with the magic link token
      const consumerBaseUrl = import.meta.env.DEV ? 'http://localhost:5174' : ''
      globalThis.open(`${consumerBaseUrl}/verify?token=${token}`, '_blank')
    } catch (err: unknown) {
      toastApiError(err, 'Failed to impersonate consumer');
    }
  }

  const totalPages = data ? Math.ceil(data.count / pageSize) : 0

  const columns: DataTableColumn<Consumer>[] = useMemo(() => [
    {
      id: 'consumer',
      header: 'Consumer',
      cell: (consumer) => (
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[10px] font-medium text-[var(--green)]">
            {(consumer.first_name || consumer.email).charAt(0).toUpperCase()}
          </div>
          <p className="leading-tight text-[var(--ink)]">
            {consumer.first_name || consumer.last_name
              ? `${consumer.first_name} ${consumer.last_name}`.trim()
              : '-'}
          </p>
        </div>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
    },
    {
      id: 'phone',
      header: 'Phone',
      cell: (consumer) => formatPhoneDisplay(consumer.phone_number) || '-',
    },
    {
      id: 'registrations',
      header: 'Registrations',
      cell: (consumer) => (
        <span className="block text-right">{consumer.registration_count}</span>
      ),
    },
    {
      id: 'joined',
      header: 'Joined',
      cell: (consumer) => (
        <span className="block text-right text-[var(--ink-3)]">
          {formatJoinedDate(consumer.created_at, formatDate)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (consumer) => (
        <div className="flex items-center justify-end gap-2">
          <button
            title={`Impersonate ${consumer.email}`}
            onClick={(e) => {
              e.stopPropagation()
              handleImpersonateConsumer(consumer.uuid)
            }}
            className="h-6 w-6 rounded-md flex items-center justify-center transition-colors duration-100 cursor-pointer text-[var(--ink-3)] hover:bg-[var(--status-warn-bg)] hover:text-[var(--status-warn)]"
          >
            <ArrowRightLeft className="h-3 w-3" />
          </button>
        </div>
      ),
    },
  ], [formatDate])

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Header bar */}
      <header
        className="flex h-14 items-center gap-4 border-b-[0.5px] border-[var(--rule)] px-6"
      >
        <button
          onClick={() => navigate('/staff/dashboard')}
          className="flex cursor-pointer items-center gap-1.5 rounded-[4px] px-2 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-colors hover:bg-[var(--bg-subtle)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Dashboard
        </button>
        <div className="h-4 w-px bg-[var(--rule)]" />
        <span className="clara-section-label tracking-[0.14em]">
          iWarranty
        </span>
        <span className="rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--green)]">
          Staff
        </span>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Title + Search */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="clara-title mb-1">Consumers</h1>
            <p className="text-[12px] text-[var(--ink-3)]">
              {data ? `${data.count} total` : '…'}
            </p>
          </div>
          <div className="w-72">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Search by name or email…"
              className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] text-[12px]"
            />
          </div>
        </div>

        {/* Table */}
        <DataTable<Consumer>
          tableId="staff-consumers"
          columns={columns}
          data={data?.results ?? []}
          onRowClick={(consumer) => navigate(`/customers/${consumer.uuid}`)}
          emptyMessage={debouncedQuery ? 'No consumers match your search' : 'No consumers yet'}
          isLoading={loading}
        />

        {/* Pagination */}
        {data && data.count > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-[var(--ink-3)]">
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, data.count)} of {data.count}
              </span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                className="cursor-pointer rounded border-[0.5px] border-[var(--rule)] bg-transparent px-1 py-0.5 text-[11px] text-[var(--ink-3)] outline-none"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-[var(--ink-3)] transition-colors hover:bg-[var(--bg-subtle)] disabled:cursor-default disabled:opacity-30"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="px-2 text-[11px] text-[var(--ink-3)]">
                {page} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-[var(--ink-3)] transition-colors hover:bg-[var(--bg-subtle)] disabled:cursor-default disabled:opacity-30"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
